/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Giriş Sayfası
 * ───────────────────────────────────────────────────────────────
 * İki sekmeli yapı: Müşteri Girişi ve Yetkili Girişi
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogIn, Eye, EyeOff, AlertCircle, Loader2, Phone, User, ShieldCheck, ShoppingCart, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  // Tab State
  const [activeTab, setActiveTab] = useState('customer'); // 'customer' | 'admin'

  // Admin Form State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Customer Form State
  const [phone, setPhone] = useState('');
  const [lastName, setLastName] = useState('');

  // General State
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const { signIn, loginAsCustomer, error: authError } = useAuth();

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!username.trim() || !password.trim()) {
      setLocalError('Kullanıcı Adı ve şifre alanları zorunludur.');
      return;
    }

    setIsLoading(true);
    try {
      await signIn(username, password);
      // Başarılı girişte RouteGuards /dashboard'a yönlendirecek
    } catch {
      // Hata zaten AuthContext tarafından authError'a yazılıyor
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!phone.trim() || !lastName.trim()) {
      setLocalError('Telefon numarası ve Soyadı alanları zorunludur.');
      return;
    }

    setIsLoading(true);
    try {
      await loginAsCustomer(phone, lastName);
      // Başarılı girişte RouteGuards /hesabim'a yönlendirecek
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || authError;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 relative overflow-hidden bg-navy-950">
      {/* ── Background Süslemeleri ── */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950" />
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-cyan-600/10 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3" />
        {/* Subtle geometric dot pattern / motif overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#14b8a6 1px, transparent 1px)", backgroundSize: "32px 32px" }}></div>
      </div>

      {/* ── Siteye Dön Butonu (Sol Üst) ── */}
      <Link
        to="/"
        className="group fixed top-0 left-0 z-50 flex items-center justify-center gap-3 py-4 px-6 m-4 sm:m-6 rounded-xl text-teal-300 bg-navy-900/50 border border-teal-500/20 hover:bg-teal-500/10 hover:border-teal-500/40 transition-all duration-300 shadow-lg relative overflow-hidden backdrop-blur-md"
      >
         <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-teal-500/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
         <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
         <span className="font-bold tracking-wide">Siteye Dön</span>
      </Link>

      {/* ── Login Card ── */}
      <div className="relative w-full max-w-md pt-12 pb-16">
        {/* Premium Logo / Başlık (Turkuaz Temalı) */}
        <div className="text-center mb-10 relative z-10">
          <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6 group">
            {/* Animasyonlu Işık Yansımaları */}
            <div className="absolute inset-0 bg-gradient-to-tr from-teal-500 to-cyan-400 rounded-2xl rotate-3 opacity-20 blur-lg group-hover:rotate-12 transition-all duration-[2s] ease-out" />
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-400 to-teal-500 rounded-2xl rotate-45 scale-75 opacity-20 blur-md group-hover:rotate-90 transition-all duration-[3s] ease-linear" />
            
            {/* Ana Logo Kartı */}
            <div className="w-full h-full bg-navy-950 border border-teal-500/30 rounded-2xl shadow-[0_0_30px_rgba(20,184,166,0.15)] flex flex-col items-center justify-center relative overflow-hidden backdrop-blur-md z-10 transition-transform group-hover:scale-105 duration-500">
              <div className="absolute top-0 left-0 w-full h-[50%] bg-gradient-to-b from-white/10 to-transparent" />
              <span className="text-[32px] font-serif text-transparent bg-clip-text bg-gradient-to-br from-white via-teal-100 to-teal-300 drop-shadow-md tracking-tighter" style={{ fontStyle: 'italic', paddingRight: '4px' }}>
                HÇ
              </span>
            </div>
          </div>
          
          <h1 className="text-[28px] font-light text-white tracking-wider drop-shadow-md">
            HASIMOĞLU
            <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400 ml-2">ÇİFTLİĞİ</span>
          </h1>
          <p className="text-[10px] font-medium text-teal-200/60 mt-3 tracking-[0.3em] uppercase relative inline-block">
            Kurban Yönetim Platformu
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent"></span>
          </p>
        </div>

        {/* Form Card */}
        <div className="glass rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/40 ring-1 ring-white/10">
          
          {/* Tabs */}
          <div className="flex bg-navy-900/50 rounded-lg p-1 mb-6 border border-navy-700/50">
            <button
              onClick={() => { setActiveTab('customer'); setLocalError(''); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'customer' 
                  ? 'bg-navy-700 text-white shadow-sm' 
                  : 'text-navy-400 hover:text-navy-200 hover:bg-navy-800/50'
              }`}
            >
              <User className="w-4 h-4" />
              Müşteri Giriş Ekranı
            </button>
            <button
              onClick={() => { setActiveTab('admin'); setLocalError(''); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'admin' 
                  ? 'bg-navy-700 text-white shadow-sm' 
                  : 'text-navy-400 hover:text-navy-200 hover:bg-navy-800/50'
              }`}
            >
              <ShieldCheck className="w-4 h-4" />
              Yetkili Girişi
            </button>
          </div>

          {/* Hata Mesajı */}
          {displayError && (
            <div className="mb-5 flex items-start gap-3 bg-danger-500/10 border border-danger-500/20 rounded-xl p-3">
              <AlertCircle className="w-5 h-5 text-danger-500 shrink-0 mt-0.5" />
              <p className="text-sm text-danger-500">{displayError}</p>
            </div>
          )}

          {/* MÜŞTERİ FORMU */}
          {activeTab === 'customer' && (
            <form onSubmit={handleCustomerSubmit} className="space-y-5 animate-in fade-in zoom-in-95 duration-200">
              <div>
                <label htmlFor="customer-phone" className="block text-sm font-medium text-navy-200 mb-1.5">
                  Telefon Numarası
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="w-5 h-5 text-navy-400" />
                  </div>
                  <input
                    id="customer-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0555 123 45 67"
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-all duration-200"
                    disabled={isLoading}
                  />
                </div>
                <p className="text-xs text-navy-400 mt-1.5">Başında '0' girmeniz veya girmemeniz fark etmez.</p>
              </div>

              <div>
                <label htmlFor="customer-lastname" className="block text-sm font-medium text-navy-200 mb-1.5">
                  Soyadı
                </label>
                <input
                  id="customer-lastname"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Kayıtlı Soyadınız"
                  className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-all duration-200"
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-[0_0_20px_rgba(20,184,166,0.2)] hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Giriş Yapılıyor...
                  </>
                ) : (
                  'Giriş Yap ve Devam Et'
                )}
              </button>
            </form>
          )}

          {/* YETKİLİ FORMU */}
          {activeTab === 'admin' && (
            <form onSubmit={handleAdminSubmit} className="space-y-5 animate-in fade-in zoom-in-95 duration-200">
              <div>
                <label htmlFor="admin-username" className="block text-sm font-medium text-navy-200 mb-1.5">
                  Kullanıcı Kimliği (ID)
                </label>
                <input
                  id="admin-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="örn: ahmet_kurtkoy"
                  autoComplete="username"
                  className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-all duration-200"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="admin-password" className="block text-sm font-medium text-navy-200 mb-1.5">
                  Şifre
                </label>
                <div className="relative">
                  <input
                    id="admin-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 transition-all duration-200"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-200 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-500 hover:to-navy-600 border border-navy-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Giriş yapılıyor...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Yönetici Girişi
                  </>
                )}
              </button>
            </form>
          )}

        </div>

        {/* Ziyaretçi / Misafir Butonu */}
        <div className="mt-6 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
          <div className="flex items-center justify-center w-full max-w-sm mb-4">
             <div className="h-px bg-navy-700/50 flex-1"></div>
             <span className="px-4 text-xs text-navy-400 uppercase tracking-widest font-semibold">Veya</span>
             <div className="h-px bg-navy-700/50 flex-1"></div>
          </div>
          <Link
            to="/katalog"
            className="group flex items-center justify-center gap-3 w-full max-w-sm py-4 rounded-xl text-gold-400 bg-navy-900/50 border border-gold-500/20 hover:bg-gold-500/10 hover:border-gold-500/40 transition-all duration-300 shadow-lg relative overflow-hidden"
          >
             <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-gold-500/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
             <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform" />
             <span className="font-bold tracking-wide">Ziyaretçi Olarak Kurbanlıkları İncele</span>
          </Link>

        </div>

        {/* Alt Bilgi */}
        <p className="text-center text-xs text-navy-500 mt-6">
          © 2026 HAŞİMOĞLU ÇİFTLİĞİ
        </p>
      </div>
    </div>
  );
}
